import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShoppingBag, Truck, CheckCircle2, XCircle, Phone, MapPin, CreditCard, Banknote, PackageCheck, Tag, Trash2, Plus, FileText, ShieldCheck, ShieldX, Mail } from "lucide-react";

const STATUSES = {
  noua: { label: "Noua", color: "bg-blue-500", border: "border-blue-500", text: "text-blue-400" },
  confirmata: { label: "Confirmata", color: "bg-amber-500", border: "border-amber-500", text: "text-amber-400" },
  expediata: { label: "Expediata", color: "bg-purple-500", border: "border-purple-500", text: "text-purple-400" },
  livrata: { label: "Livrata", color: "bg-green-500", border: "border-green-500", text: "text-green-400" },
  anulata: { label: "Anulata", color: "bg-red-600", border: "border-red-600", text: "text-red-400" },
};

const fmtRON = (v) => `${new Intl.NumberFormat("ro-RO").format(v)} RON`;

const PayBadge = ({ o }) => {
  if (o.payment_method === "ramburs")
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-neutral-800 px-2 py-0.5"><Banknote className="w-3 h-3" /> Ramburs</span>;
  if (o.payment_status === "paid")
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-green-950 text-green-400 border border-green-900 px-2 py-0.5"><CreditCard className="w-3 h-3" /> Card · Platit</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-red-950 text-red-400 border border-red-900 px-2 py-0.5"><CreditCard className="w-3 h-3" /> Card · Neplatit</span>;
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [shipTarget, setShipTarget] = useState(null);
  const [awb, setAwb] = useState("");
  const [promoOpen, setPromoOpen] = useState(false);
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", type: "percent", value: "", max_uses: "" });

  const loadPromos = async () => setPromos((await api.get("/admin/promos")).data);

  const createPromo = async (e) => {
    e.preventDefault();
    if (!promoForm.code || !promoForm.value) { toast.error("Completeaza codul si valoarea"); return; }
    try {
      await api.post("/admin/promos", {
        code: promoForm.code, type: promoForm.type,
        value: parseFloat(promoForm.value), max_uses: parseInt(promoForm.max_uses || 0, 10),
      });
      toast.success("Cod promo creat");
      setPromoForm({ code: "", type: "percent", value: "", max_uses: "" });
      loadPromos();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare"); }
  };

  const load = async () => setOrders((await api.get("/admin/orders")).data);
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status, extra = {}) => {
    try {
      await api.patch(`/admin/orders/${o.id}`, { status, ...extra });
      toast.success(`Comanda ${o.order_number}: ${STATUSES[status].label}`);
      load();
    } catch { toast.error("Eroare"); }
  };

  const ship = async (e) => {
    e.preventDefault();
    if (!awb.trim()) { toast.error("Introdu AWB-ul"); return; }
    await setStatus(shipTarget, "expediata", { awb: awb.trim() });
    setShipTarget(null);
    setAwb("");
  };

  const deleteOrder = async (o) => {
    if (!confirm(`Stergi DEFINITIV comanda ${o.order_number} (${o.customer_name})? Nu se poate recupera. Stocul se returneaza daca era rezervat.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success(`Comanda ${o.order_number} stearsa definitiv`);
      load();
    } catch { toast.error("Eroare la stergere"); }
  };

  const counts = orders.reduce((a, o) => { a[o.status] = (a[o.status] || 0) + 1; return a; }, {});
  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl uppercase">Comenzi</h1>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">Comenzile din magazinul Troti-X</div>
        </div>
        <div className="flex items-center gap-4">
          <Button data-testid="promo-manager-btn" onClick={() => { setPromoOpen(true); loadPromos(); }}
            className="bg-neutral-900 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-neutral-800 rounded-none uppercase tracking-wider font-bold text-xs">
            <Tag className="w-4 h-4 mr-2" /> Coduri promo
          </Button>
          <div className="text-right">
            <div className="text-2xl font-bold text-[#00E5FF]">{counts.noua || 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Comenzi noi</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {Object.entries(STATUSES).map(([k, s]) => (
          <button key={k} data-testid={`order-stat-${k}`} onClick={() => setFilter(filter === k ? "all" : k)}
            className={`border p-3 text-left transition-colors ${filter === k ? `${s.border} bg-neutral-900` : "border-neutral-800 hover:border-neutral-600"}`}>
            <div className={`text-2xl font-bold ${s.text}`}>{counts[k] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1">{s.label}</div>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="border border-dashed border-neutral-800 p-16 text-center text-neutral-500">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-neutral-700" />
            Nicio comanda {filter !== "all" ? "cu acest status" : "momentan"}.
          </div>
        )}
        {filtered.map((o) => {
          const s = STATUSES[o.status] || STATUSES.noua;
          return (
            <div key={o.id} data-testid={`order-card-${o.order_number}`} className={`border border-neutral-800 bg-neutral-950 border-l-4 ${s.border}`}>
              <div className="p-4 flex flex-wrap gap-4">
                <div className="min-w-[110px]">
                  <div className="font-mono font-bold text-[#00E5FF]">{o.order_number}</div>
                  <span className={`inline-block mt-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold text-black ${s.color}`}>{s.label}</span>
                  <div className="mt-2"><PayBadge o={o} /></div>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-semibold">{o.customer_name}</div>
                  <div className="text-xs text-neutral-400 flex items-center gap-1.5 mt-1"><Phone className="w-3 h-3" /> {o.phone}</div>
                  {o.email && <div data-testid={`order-email-${o.order_number}`} className="text-xs text-neutral-400 flex items-center gap-1.5 mt-0.5"><Mail className="w-3 h-3" /> <a href={`mailto:${o.email}`} className="hover:text-[#00E5FF]">{o.email}</a></div>}
                  <div className="text-xs text-neutral-400 flex items-center gap-1.5 mt-0.5"><MapPin className="w-3 h-3" /> {o.address}, {o.city}{o.county ? `, ${o.county}` : ""}</div>
                  {o.note && <div className="text-xs text-neutral-500 italic mt-1">"{o.note}"</div>}
                  <div className="mt-2 space-y-0.5">
                    {o.items.map((it, i) => (
                      <div key={i} className="text-sm text-neutral-300">{it.qty} × {it.name} <span className="text-neutral-500">({fmtRON(it.price)})</span></div>
                    ))}
                  </div>
                </div>
                <div className="text-right min-w-[130px]">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total</div>
                  <div className="text-xl font-bold text-[#00E5FF]">{fmtRON(o.total)}</div>
                  {o.discount > 0 && (
                    <div className="text-xs text-green-400 mt-0.5">-{fmtRON(o.discount)} ({o.promo_code})</div>
                  )}
                  <div className="text-[10px] text-neutral-600 mt-1">{new Date(o.created_at).toLocaleString("ro-RO")}</div>
                  {o.awb && <div className="text-xs font-mono mt-1.5">AWB: {o.awb}</div>}
                  <div className="mt-2">
                    {o.warranty_cancelled ? (
                      <div data-testid={`warranty-info-${o.order_number}`} className="inline-flex items-center gap-1.5 bg-red-950/60 border border-red-900 text-red-300 text-[10px] uppercase tracking-wider px-2 py-1" title={o.warranty_cancel_reason}>
                        <ShieldX className="w-3 h-3" /> Garantie ANULATA ({o.warranty_ref})
                      </div>
                    ) : (
                      <div data-testid={`warranty-info-${o.order_number}`} className={`inline-flex items-center gap-1.5 border text-[10px] uppercase tracking-wider px-2 py-1 ${o.warranty_days_left > 90 ? "bg-green-950/50 border-green-900 text-green-400" : "bg-amber-950/50 border-amber-900 text-amber-400"}`}>
                        <ShieldCheck className="w-3 h-3" /> Garantie: {o.warranty_days_left > 0 ? `${Math.floor(o.warranty_days_left / 30)} luni ramase` : "EXPIRATA"}
                      </div>
                    )}
                  </div>
                  {o.warranty_cancelled && o.warranty_cancel_reason && (
                    <div className="text-[10px] text-red-400/80 mt-1">Motiv: {o.warranty_cancel_reason}</div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 px-4 pb-3 border-t border-neutral-900 pt-3">
                {o.status === "noua" && (
                  <Button size="sm" data-testid={`confirm-order-${o.order_number}`} onClick={() => setStatus(o, "confirmata")}
                    className="bg-amber-500 text-black hover:bg-amber-400 rounded-none uppercase text-xs tracking-wider font-bold">
                    <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Confirma
                  </Button>
                )}
                {["noua", "confirmata"].includes(o.status) && (
                  <Button size="sm" data-testid={`ship-order-${o.order_number}`} onClick={() => { setShipTarget(o); setAwb(o.awb || ""); }}
                    className="bg-purple-500 text-black hover:bg-purple-400 rounded-none uppercase text-xs tracking-wider font-bold">
                    <Truck className="w-3.5 h-3.5 mr-1.5" /> Expediaza (AWB)
                  </Button>
                )}
                {o.status === "expediata" && (
                  <Button size="sm" data-testid={`deliver-order-${o.order_number}`} onClick={() => setStatus(o, "livrata")}
                    className="bg-green-500 text-black hover:bg-green-400 rounded-none uppercase text-xs tracking-wider font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Marcheaza livrata
                  </Button>
                )}
                <button data-testid={`invoice-order-${o.order_number}`} onClick={() => window.open(`/admin/comenzi/${o.id}/factura`, "_blank")}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-neutral-400 hover:text-[#00E5FF] transition-colors px-2 py-1.5">
                  <FileText className="w-3.5 h-3.5" /> Factura
                </button>
                {!["anulata", "livrata"].includes(o.status) && (
                  <button data-testid={`cancel-order-${o.order_number}`} onClick={() => { if (confirm("Anulezi comanda? Stocul se returneaza.")) setStatus(o, "anulata"); }}
                    className="ml-auto inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-neutral-500 hover:text-red-400 transition-colors">
                    <XCircle className="w-3.5 h-3.5" /> Anuleaza
                  </button>
                )}
                <button data-testid={`delete-order-${o.order_number}`} onClick={() => deleteOrder(o)}
                  className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-neutral-600 hover:text-red-500 transition-colors ${["anulata", "livrata"].includes(o.status) ? "ml-auto" : ""}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Sterge definitiv
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><Tag className="w-5 h-5 text-[#00E5FF]" /> Coduri promo</DialogTitle></DialogHeader>
          <form onSubmit={createPromo} className="border border-neutral-800 p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-neutral-400">Cod (ex: VARA10)</Label>
                <Input data-testid="promo-code-create-input" value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  className="bg-black border-neutral-800 rounded-none mt-1 font-mono uppercase" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-neutral-400">Tip reducere</Label>
                <Select value={promoForm.type} onValueChange={(v) => setPromoForm({ ...promoForm, type: v })}>
                  <SelectTrigger data-testid="promo-type-select" className="bg-black border-neutral-800 rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                    <SelectItem value="percent">Procent (%)</SelectItem>
                    <SelectItem value="fixed">Suma fixa (RON)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-neutral-400">{promoForm.type === "percent" ? "Procent (%)" : "Suma (RON)"}</Label>
                <Input data-testid="promo-value-input" type="number" step="0.01" value={promoForm.value} onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })}
                  className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-neutral-400">Utilizari maxime (0 = nelimitat)</Label>
                <Input data-testid="promo-maxuses-input" type="number" value={promoForm.max_uses} onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                  className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
            </div>
            <Button data-testid="promo-create-btn" type="submit" className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold w-full">
              <Plus className="w-4 h-4 mr-2" /> Creeaza codul
            </Button>
          </form>
          <div className="space-y-2">
            {promos.length === 0 && <div className="text-center text-neutral-500 text-sm py-4">Niciun cod promo creat.</div>}
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-row-${p.code}`} className={`flex items-center gap-3 border border-neutral-800 px-3 py-2.5 ${!p.active ? "opacity-50" : ""}`}>
                <div className="font-mono font-bold text-[#00E5FF]">{p.code}</div>
                <div className="text-sm text-neutral-300">{p.type === "percent" ? `-${p.value}%` : `-${fmtRON(p.value)}`}</div>
                <div className="text-xs text-neutral-500">folosit {p.uses}x{p.max_uses > 0 ? ` / max ${p.max_uses}` : ""}</div>
                <div className="ml-auto flex items-center gap-2">
                  <Switch data-testid={`promo-toggle-${p.code}`} checked={p.active} onCheckedChange={async () => { await api.patch(`/admin/promos/${p.id}`); loadPromos(); }} />
                  <button data-testid={`promo-delete-${p.code}`} onClick={async () => { if (confirm(`Sterge codul ${p.code}?`)) { await api.delete(`/admin/promos/${p.id}`); loadPromos(); } }}
                    className="p-1.5 text-neutral-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shipTarget} onOpenChange={(v) => !v && setShipTarget(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Expediere · {shipTarget?.order_number}</DialogTitle></DialogHeader>
          <form onSubmit={ship} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">AWB curier *</Label>
              <Input data-testid="order-awb-input" value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="ex: 2853100401234"
                className="bg-black border-neutral-800 rounded-none mt-1 font-mono" autoFocus />
            </div>
            <DialogFooter>
              <Button data-testid="order-ship-save-btn" type="submit" className="bg-purple-500 text-black hover:bg-purple-400 rounded-none uppercase tracking-wider font-bold w-full">
                <Truck className="w-4 h-4 mr-2" /> Marcheaza ca expediata
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
